import { type Row as TableRow } from '@ardoq/table';
import { type Locale, localeIndexOfLowercase } from '@ardoq/locale';

export type FolderGroupData = { name: string; isNoFolder?: boolean };

export type FolderRowData = {
  _group: FolderGroupData;
};

export const getMatchingRows = (
  rows: TableRow<FolderRowData>[],
  searchPhrase: string,
  locale: Locale
): TableRow<FolderRowData>[] =>
  rows.filter(
    row =>
      localeIndexOfLowercase(row.data._group.name, searchPhrase, locale) !== -1
  );

const getPath = (row: TableRow<FolderRowData>): string[] =>
  row.path ?? [row.id];

export const getAncestorIds = (rows: TableRow<FolderRowData>[]): string[] => [
  ...new Set(rows.flatMap(row => getPath(row).slice(0, -1))),
];

type SearchFolderRowsArgs = {
  rows: TableRow<FolderRowData>[];
  searchPhrase: string;
  selectedRowId: string;
  locale: Locale;
};

export type FolderSearchResult = {
  matchCount: number;
  rowsToRender: TableRow<FolderRowData>[];
  /**
   * The folders that have to be open for every match and the current selection
   * to be reachable. Subtrees hanging off a match are deliberately absent —
   * they render collapsed, so a broad phrase does not unfold the whole tree.
   */
  expandedIds: string[];
};

export const searchFolderRows = ({
  rows,
  searchPhrase,
  selectedRowId,
  locale,
}: SearchFolderRowsArgs): FolderSearchResult => {
  if (searchPhrase === '') {
    return { matchCount: 0, rowsToRender: rows, expandedIds: [] };
  }

  const matchingRows = getMatchingRows(rows, searchPhrase, locale);
  const matchingIds = new Set(matchingRows.map(row => row.id));
  const selectedRows = matchingIds.has(selectedRowId)
    ? []
    : rows.filter(row => row.id === selectedRowId);

  const idsToKeep = new Set(
    [...matchingRows, ...selectedRows].flatMap(getPath)
  );
  const rowsToRender = rows.filter(
    row =>
      idsToKeep.has(row.id) ||
      getPath(row).some(ancestorId => matchingIds.has(ancestorId))
  );

  return {
    matchCount: matchingRows.length,
    rowsToRender,
    expandedIds: getAncestorIds([...matchingRows, ...selectedRows]),
  };
};

export type ExpansionState = {
  manualIds: string[];
  collapsedIds: string[];
};

type ResolveExpandedIdsArgs = ExpansionState & {
  searchIds: string[];
};

export const resolveExpandedIds = ({
  manualIds,
  searchIds,
  collapsedIds,
}: ResolveExpandedIdsArgs): string[] => {
  const collapsed = new Set(collapsedIds);
  return [...new Set([...manualIds, ...searchIds])].filter(
    id => !collapsed.has(id)
  );
};

/**
 * `collapsedIds` exists only to override the search's derived expansions, so it
 * is dropped whenever those are re-derived — otherwise a collapse made under one
 * phrase keeps masking matches found by the next.
 */
export const clearSearchMask = (expansion: ExpansionState): ExpansionState => ({
  ...expansion,
  collapsedIds: [],
});

type ToggleExpansionArgs = {
  expansion: ExpansionState;
  expandedIds: string[];
  rowId: string;
  isExpanded: boolean;
};

/**
 * AG Grid dispatches `rowGroupOpened` for programmatic expansion as well as for
 * chevron clicks, so a toggle that merely agrees with the ids we last handed the
 * grid is an echo of our own sync and leaves the state untouched.
 */
export const toggleExpansion = ({
  expansion,
  expandedIds,
  rowId,
  isExpanded,
}: ToggleExpansionArgs): ExpansionState => {
  if (isExpanded === expandedIds.includes(rowId)) return expansion;

  if (isExpanded) {
    return {
      manualIds: [...expansion.manualIds, rowId],
      collapsedIds: expansion.collapsedIds.filter(id => id !== rowId),
    };
  }

  return {
    manualIds: expansion.manualIds.filter(id => id !== rowId),
    collapsedIds: [...expansion.collapsedIds, rowId],
  };
};

export const isRowVisible = (
  row: TableRow<FolderRowData>,
  expandedIds: ReadonlySet<string>
): boolean =>
  getPath(row)
    .slice(0, -1)
    .every(ancestorId => expandedIds.has(ancestorId));

export const countVisibleRows = (
  rows: TableRow<FolderRowData>[],
  expandedIds: string[]
): number => {
  const expanded = new Set(expandedIds);
  return rows.filter(row => isRowVisible(row, expanded)).length;
};
